import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import Login from './Login';
import useAuthSession from '../../hooks/useAuthSession';
import { post } from '../../helpers/fetchServicesMethods';
import { url } from '../../reducers/url';
import { oauthConfig } from '../../config';

/**
 * OAuthAuthorize — the GQLS login step of the OAuth flow initiated by the
 * search-mcp Claude connector (spec 003-mcp-connector-oauth-login).
 *
 * Flow:
 *   1. Parse + validate the OAuth query params (FR-3). Invalid -> generic error,
 *      no login form, no backend call (SC-3).
 *   2. If a GQ session is already active (useAuthSession) -> skip the form and
 *      mint immediately (Scenario 2, silent SSO).
 *   3. Otherwise render the existing <Login> form; on success the session flips
 *      to logged-in and we mint (Scenario 1). Login's own '/home' redirect is
 *      suppressed via the onLoginSuccess prop.
 *   4. Mint via do=gqoauth.mint_code using the existing session cookie (FR-6),
 *      then a full-page redirect back to redirect_uri with ?code=&state= (FR-7).
 *   5. A failed mint shows a retry-able error and never redirects (FR-8, SC-4).
 *
 * Security note: the client_id / redirect_uri checks here are a fast-fail UX
 * nicety, NOT the security boundary. The backend (do=gqoauth.mint_code) is the
 * real enforcement point and MUST re-validate redirect_uri (exact match) and
 * bind the minted code to the PKCE challenge + the authenticated user.
 */

const STATUS = {
    INVALID: 'invalid',
    READY: 'ready',            // params valid; waiting for session, then minting
    MINTING: 'minting',
    MINT_ERROR: 'mint_error',
    REDIRECTING: 'redirecting',
};

function parseParams(inHashSearch, fallbackSearch) {
    // HashRouter carries the query after the in-hash path
    // (…/#/oauth-authorize?client_id=…), so useLocation().search has it. Fall
    // back to the real query string (…?client_id=…#/oauth-authorize) just in case.
    const raw = (inHashSearch && inHashSearch.length > 1) ? inHashSearch : (fallbackSearch || '');
    const p = new URLSearchParams(raw);
    return {
        clientId: p.get('client_id') || '',
        redirectUri: p.get('redirect_uri') || '',
        state: p.get('state'),                       // may be null; opaque passthrough
        codeChallenge: p.get('code_challenge') || '',
        codeChallengeMethod: p.get('code_challenge_method') || '',
        responseType: p.get('response_type') || '',
    };
}

function isValid(params) {
    if (!params.clientId || params.clientId !== oauthConfig.clientId) return false;
    if (!params.redirectUri || !oauthConfig.redirectUriAllowList.includes(params.redirectUri)) return false;
    if (params.responseType !== 'code') return false;
    if (params.codeChallengeMethod !== 'S256') return false;
    if (!params.codeChallenge) return false;
    return true;
}

export default function OAuthAuthorize() {
    const location = useLocation();
    const authSession = useAuthSession();

    // Parse once — the incoming params are fixed for the life of this mount.
    const paramsRef = useRef(null);
    if (paramsRef.current === null) {
        paramsRef.current = parseParams(
            location.search,
            typeof window !== 'undefined' ? window.location.search : ''
        );
    }
    const params = paramsRef.current;

    const [status, setStatus] = useState(() => (isValid(params) ? STATUS.READY : STATUS.INVALID));
    const [errorText, setErrorText] = useState('');
    const mintStarted = useRef(false);

    const buildRedirect = useCallback((code) => {
        const sep = params.redirectUri.includes('?') ? '&' : '?';
        let target = `${params.redirectUri}${sep}code=${encodeURIComponent(code)}`;
        if (params.state != null) {
            target += `&state=${encodeURIComponent(params.state)}`;
        }
        return target;
    }, [params]);

    const mintCode = useCallback(async () => {
        setStatus(STATUS.MINTING);
        setErrorText('');

        // Session cookie is attached automatically by post()'s axios config —
        // no explicit credential is sent (FR-6). state is intentionally omitted.
        const body = new FormData();
        body.append('client_id', params.clientId);
        body.append('redirect_uri', params.redirectUri);
        body.append('code_challenge', params.codeChallenge);
        body.append('code_challenge_method', params.codeChallengeMethod);

        try {
            const resp = await post(url.mintOAuthCode, body);
            const code = (resp && resp.response_status === 0 && resp.response_content)
                ? resp.response_content.code
                : null;

            if (code) {
                setStatus(STATUS.REDIRECTING);
                window.location.href = buildRedirect(code);   // FR-7: full-page nav
            } else {
                // FR-8 / SC-4: visible, retry-able error; do NOT redirect.
                setErrorText('We could not complete the authorization. Please try again.');
                setStatus(STATUS.MINT_ERROR);
            }
        } catch (e) {
            setErrorText('We could not complete the authorization. Please try again.');
            setStatus(STATUS.MINT_ERROR);
        }
    }, [params, buildRedirect]);

    // Drive minting off the session state so Scenario 1 (log in, then the
    // session flips) and Scenario 2 (already logged in on mount) are handled
    // uniformly, and exactly once.
    useEffect(() => {
        if (status === STATUS.READY && authSession.isLoggedIn && !mintStarted.current) {
            mintStarted.current = true;
            mintCode();
        }
    }, [status, authSession.isLoggedIn, mintCode]);

    // Suppress <Login>'s default '/home' redirect on this route. Minting is
    // driven by the session-state effect above (which fires once), so the
    // callback itself is a no-op — it exists only to override navigation.
    const handleLoginSuccess = useCallback(() => {}, []);

    // --- Invalid request (Scenario 3 / SC-3): no form, no backend call. ---
    if (status === STATUS.INVALID) {
        return (
            <div style={styles.wrap}>
                <div style={styles.card}>
                    <h5 style={styles.title}>Invalid authorization request</h5>
                    <p style={styles.muted}>
                        This authorization link is missing required information or is not valid.
                    </p>
                </div>
            </div>
        );
    }

    // --- Params valid, no active session yet: reuse the existing login form. ---
    if (!authSession.isLoggedIn) {
        return <Login onLoginSuccess={handleLoginSuccess} />;
    }

    // --- Logged in: minting / redirecting, with a retry on failure. ---
    return (
        <div style={styles.wrap}>
            <div style={styles.card}>
                {status === STATUS.MINT_ERROR ? (
                    <React.Fragment>
                        <h5 style={styles.title}>Authorization failed</h5>
                        <p style={styles.muted}>{errorText}</p>
                        <button
                            type="button"
                            style={styles.button}
                            onClick={() => mintCode()}
                        >
                            Try again
                        </button>
                    </React.Fragment>
                ) : (
                    <p style={styles.title}>Authorizing&hellip;</p>
                )}
            </div>
        </div>
    );
}

const styles = {
    wrap: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        padding: 24,
    },
    card: {
        maxWidth: 440,
        textAlign: 'center',
        padding: '24px 28px',
        border: '1px solid #e2e5ea',
        borderRadius: 8,
    },
    title: { margin: 0, fontWeight: 600 },
    muted: { marginTop: 8, marginBottom: 0, color: '#5b6472' },
    button: {
        marginTop: 16,
        padding: '8px 16px',
        border: '1px solid #008EC5',
        background: '#008EC5',
        color: '#fff',
        borderRadius: 4,
        cursor: 'pointer',
    },
};
